import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, CheckCircle2, ShieldAlert } from 'lucide-react';
import TermsConsentCheckbox from '../components/TermsConsentCheckbox';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AcceptTeamInvite = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [state, setState] = useState({ loading: true, invite: null, error: null });
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/team-invitations/validate/${token}`);
        setState({ loading: false, invite: data, error: null });
      } catch (err) {
        const detail = err.response?.data?.detail || 'This invitation is invalid or expired.';
        setState({ loading: false, invite: null, error: detail });
      }
    })();
  }, [token]);

  const [acceptTerms, setAcceptTerms] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!acceptTerms) { toast.error('Please accept the Terms of Service and Privacy Policy'); return; }
    if (password !== confirm) {
      toast.error('Passwords do not match');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/team-invitations/accept`,
        { token, password, accept_terms: acceptTerms },
        { withCredentials: true });
      toast.success('Welcome aboard!');
      navigate('/dashboard');
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Could not accept invitation');
    } finally {
      setSubmitting(false);
    }
  };

  if (state.loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="w-6 h-6 animate-spin text-[#002FA7]" />
      </div>
    );
  }

  if (state.error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4" data-testid="accept-invite-error">
        <div className="max-w-md w-full bg-white border border-slate-200 rounded-md p-8 text-center shadow-sm">
          <ShieldAlert className="w-10 h-10 text-red-500 mx-auto mb-3" />
          <h1 className="text-xl font-bold text-slate-900" style={{ fontFamily: 'Work Sans, sans-serif' }}>
            Invitation unavailable
          </h1>
          <p className="text-sm text-slate-600 mt-2">{state.error}</p>
          <p className="text-xs text-slate-500 mt-6">
            If you think this is a mistake, ask your team admin to send you a fresh invite.
          </p>
        </div>
      </div>
    );
  }

  const { invite } = state;
  const roleLabel = invite.role === 'company_admin' ? 'Company Admin' : 'Dispatcher';

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4" data-testid="accept-invite-page">
      <div className="max-w-md w-full bg-white border border-slate-200 rounded-md shadow-sm">
        <div className="px-8 pt-8 pb-4 border-b border-slate-100">
          <p className="text-xs font-semibold uppercase tracking-widest text-[#002FA7]">You're invited</p>
          <h1 className="text-3xl font-bold text-slate-900 mt-1" style={{ fontFamily: 'Work Sans, sans-serif' }}>
            Join {invite.company_name}
          </h1>
          <p className="text-sm text-slate-600 mt-2">
            Set a password to activate your <strong>{roleLabel}</strong> account for
            <span className="text-slate-900"> {invite.email}</span>.
          </p>
        </div>
        <form onSubmit={submit} className="px-8 py-6 space-y-4">
          <div>
            <label className="text-xs font-semibold uppercase tracking-widest text-slate-500">New password</label>
            <input
              data-testid="accept-password"
              type="password" required minLength={8}
              value={password} onChange={(e) => setPassword(e.target.value)}
              className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-md focus:ring-2 focus:ring-[#002FA7] focus:outline-none"
            />
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-widest text-slate-500">Confirm password</label>
            <input
              data-testid="accept-password-confirm"
              type="password" required minLength={8}
              value={confirm} onChange={(e) => setConfirm(e.target.value)}
              className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-md focus:ring-2 focus:ring-[#002FA7] focus:outline-none"
            />
          </div>
          <TermsConsentCheckbox checked={acceptTerms} onChange={setAcceptTerms} testid="accept-terms-checkbox" compact />
          <button
            type="submit" disabled={submitting || !acceptTerms}
            data-testid="accept-submit"
            className="w-full mt-4 flex items-center justify-center gap-2 bg-[#002FA7] text-white font-semibold py-2.5 rounded-md hover:bg-[#00247D] disabled:opacity-50"
          >
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
            Activate my account
          </button>
          <p className="text-xs text-slate-500 text-center mt-2">
            Password must be at least 8 characters and include a mix of letters, numbers, and symbols.
          </p>
        </form>
      </div>
    </div>
  );
};

export default AcceptTeamInvite;
